import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { getUserPermissions, updateUserPreference } from '../services/permissionService'

const NotificationContext = createContext()
const NOTIFICATION_PREFERENCES_KEY = 'notificationPreferences'

const defaultPreferences = () => ({
  stockAlerts: true,
  deviceOffline: true,
  soundEnabled: false,
  criticalOnly: false,
  quietHours: { enabled: false, start: '22:00', end: '08:00' }
})

function loadPreferences() {
  const defaults = defaultPreferences()

  try {
    const saved = window.localStorage.getItem(NOTIFICATION_PREFERENCES_KEY)
    if (!saved) return defaults

    const parsed = JSON.parse(saved)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Saved notification preferences are not an object')
    }

    return {
      ...defaults,
      ...parsed,
      quietHours: {
        ...defaults.quietHours,
        ...(parsed.quietHours && typeof parsed.quietHours === 'object'
          ? parsed.quietHours
          : {})
      }
    }
  } catch (error) {
    console.warn('Using default notification preferences:', error.message)
    return defaults
  }
}

function savePreferences(preferences) {
  try {
    window.localStorage.setItem(NOTIFICATION_PREFERENCES_KEY, JSON.stringify(preferences))
  } catch (error) {
    console.warn('Could not save notification preferences:', error.message)
  }
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return context
}

export function NotificationProvider({ children }) {
  const { currentUser } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [toastsEnabled, setToastsEnabled] = useState(false) // Default disabled
  const [preferences, setPreferences] = useState(loadPreferences)

  // Load toastsEnabled preference from Firebase
  useEffect(() => {
    let isCurrent = true

    const loadToastsPreference = async () => {
      if (!currentUser?.customClaims?.customer || !currentUser?.email) {
        if (isCurrent) {
          setToastsEnabled(false)
        }
        return
      }

      try {
        const customer = currentUser.customClaims.customer
        const userPermissions = await getUserPermissions(customer, currentUser.email)
        if (!isCurrent) return

        if (userPermissions?.alert_thresholds?.enabled?.toastsEnabled !== undefined) {
          setToastsEnabled(userPermissions.alert_thresholds.enabled.toastsEnabled)
        } else {
          setToastsEnabled(false)
        }
      } catch (error) {
        if (isCurrent) {
          setToastsEnabled(false)
        }
        console.warn('Could not load notification preferences:', error.message)
      }
    }
    
    loadToastsPreference()

    return () => {
      isCurrent = false
    }
  }, [currentUser])

  // Save preferences to localStorage
  useEffect(() => {
    savePreferences(preferences)
  }, [preferences])

  // Check if notifications are in quiet hours
  const isQuietTime = () => {
    if (!preferences.quietHours.enabled) return false
    
    const now = new Date()
    const currentTime = now.getHours() * 60 + now.getMinutes()
    const [startHour, startMin] = preferences.quietHours.start.split(':').map(Number)
    const [endHour, endMin] = preferences.quietHours.end.split(':').map(Number)
    const startTime = startHour * 60 + startMin
    const endTime = endHour * 60 + endMin
    
    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime
    } else {
      return currentTime >= startTime || currentTime <= endTime
    }
  }

  // Add notification
  const addNotification = useCallback((notification) => {
    // Use timestamp + random suffix to prevent duplicate keys
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newNotification = {
      id,
      timestamp: new Date().toISOString(),
      read: false,
      dismissed: false,
      ...notification
    }

    // Check preferences - allow system notifications by default
    const categoryEnabled = preferences[notification.category] !== false
    
    if (!categoryEnabled && notification.category !== 'system') {
      return
    }
    if (preferences.criticalOnly && notification.priority !== 'critical') {
      return
    }
    if (isQuietTime() && notification.priority !== 'critical') {
      return
    }

    setNotifications(prev => [newNotification, ...prev.slice(0, 49)]) // Keep only 50 notifications

    // Browser notification
    try {
      if ('Notification' in window && window.Notification.permission === 'granted') {
        new window.Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: notification.category,
          requireInteraction: notification.priority === 'critical'
        })
      }
    } catch (error) {
      console.warn('Could not show browser notification:', error.message)
    }

    // Sound alert for critical notifications
    if (preferences.soundEnabled && notification.priority === 'critical') {
      playNotificationSound()
    }

    return id
  }, [preferences])

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DiuWkfCQ==')
      audio.volume = 0.3
      audio.play().catch(() => {}) // Ignore if sound fails
    } catch (error) {
      // Ignore sound errors
    }
  }

  // Mark notification as read
  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    )
  }

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  // Dismiss notification
  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

  // Clear all notifications
  const clearAll = () => {
    setNotifications([])
  }

  // Get unread count
  const unreadCount = notifications.filter(n => !n.read).length

  // Request notification permission
  const requestPermission = useCallback(async () => {
    try {
      if (!('Notification' in window) || typeof window.Notification.requestPermission !== 'function') {
        return false
      }

      const permission = await window.Notification.requestPermission()
      return permission === 'granted'
    } catch (error) {
      console.warn('Notification permission is unavailable:', error.message)
      return false
    }
  }, [])

  // Toggle toasts enabled/disabled
  const toggleToasts = async () => {
    if (currentUser?.customClaims?.customer && currentUser?.email) {
      const customer = currentUser.customClaims.customer
      const newValue = !toastsEnabled
      
      // Optimistically update UI
      setToastsEnabled(newValue)
      
      // Update in Firebase
      const success = await updateUserPreference(customer, currentUser.email, 'toastsEnabled', newValue)
      
      if (!success) {
        // Revert on failure
        setToastsEnabled(!newValue)
        console.error('Failed to update toast preference')
      }
    }
  }

  const value = {
    notifications,
    preferences,
    setPreferences,
    toastsEnabled,
    toggleToasts,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
    unreadCount,
    requestPermission
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}